import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowLeftRight,
  BarChart3,
  BookOpen,
  DatabaseZap,
  Euro,
  FileBarChart2,
  GitBranch,
  HeartHandshake,
  LayoutDashboard,
  ListChecks,
  MailOpen,
  MapPin,
  MessageSquare,
  UserRoundCheck,
  PlusCircle,
  Settings,
  ShieldAlert,
  TrendingUp,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getCsrfToken } from "@/integrations/php/client";

interface AdminSidebarProps {
  onNavigate?: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  count?: number;
}

const AdminSidebar = ({ onNavigate }: AdminSidebarProps) => {
  const location = useLocation();
  const [operationsCount, setOperationsCount] = React.useState(0);

  React.useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const response = await fetch("/api/admin/operations.php?summary=1", {
          credentials: "include",
          headers: { "X-CSRF-Token": await getCsrfToken() },
        });
        const body = await response.json().catch(() => ({}));
        if (active && response.ok) setOperationsCount(Number(body.data?.summary?.open || 0));
      } catch {
        // The counter is best-effort; the Operations page remains authoritative.
      }
    };
    void refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, []);

  const groups: Array<{ label: string; items: NavItem[] }> = [
    {
      label: "Καθημερινή εργασία",
      items: [
        { label: "Γενική εικόνα", path: "/admin", icon: LayoutDashboard, exact: true },
        { label: "Operations Inbox", path: "/admin/operations", icon: ShieldAlert, exact: true, count: operationsCount },
        { label: "Όλα τα leads", path: "/admin/leads", icon: ListChecks, exact: true },
        { label: "Νέο lead", path: "/admin/leads/new", icon: PlusCircle, exact: true },
        { label: "Αναθέσεις", path: "/admin/leads/assignment", icon: ArrowLeftRight, exact: true },
        { label: "Συνεργάτες", path: "/admin/partners", icon: HeartHandshake, exact: true },
        { label: "Χάρτης δικτύου", path: "/admin/partners/map", icon: MapPin, exact: true },
      ],
    },
    {
      label: "Επικοινωνία",
      items: [
        { label: "Partner Care", path: "/admin/partner-care", icon: UserRoundCheck, exact: true },
        { label: "Newsletter", path: "/admin/newsletter", icon: MailOpen, exact: true },
        { label: "SMS & email log", path: "/admin/messages", icon: MessageSquare, exact: true },
        { label: "Χρεώσεις", path: "/admin/billing", icon: Euro, exact: true },
      ],
    },
    {
      label: "Αναφορές",
      items: [
        { label: "Γενικές", path: "/admin/reports", icon: FileBarChart2, exact: true },
        { label: "Συνεργατών", path: "/admin/reports/partners", icon: TrendingUp, exact: true },
        { label: "Στατιστικά SMS", path: "/admin/messages/statistics", icon: BarChart3, exact: true },
        { label: "Στατιστικά email", path: "/admin/email/statistics", icon: MailOpen, exact: true },
      ],
    },
    {
      label: "Σύστημα",
      items: [
        { label: "Meta Lead Hub", path: "/admin/leads/meta", icon: DatabaseZap, exact: true },
        { label: "Ρυθμίσεις", path: "/admin/settings", icon: Settings, exact: true },
        { label: "Διαχειριστές", path: "/admin/admins", icon: Users, exact: true },
      ],
    },
  ];

  const isActive = (item: NavItem) =>
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  return (
    <aside className="flex h-screen w-full flex-col bg-slate-950 text-slate-100 md:sticky md:top-0 md:w-[236px] md:shrink-0">
      <div className="flex h-24 items-center border-b border-white/10 px-4">
        <Link
          to="/admin"
          onClick={onNavigate}
          aria-label="Αρχική διαχείρισης Diathermiki.gr"
          className="flex min-w-0 items-center gap-3 rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-4 focus-visible:ring-offset-slate-950"
        >
          <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-1 shadow-sm ring-1 ring-white/15">
            <img
              src="https://diathermiki.gr/favicon.jpg"
              alt="Diathermiki.gr"
              className="h-full w-full rounded-lg object-contain"
              decoding="async"
              loading="eager"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = "/logo.svg";
              }}
            />
          </span>
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold tracking-wide text-white">diathermiki.gr</span>
            <span className="mt-0.5 block text-xs text-slate-400">Lead Operations</span>
          </span>
        </Link>
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-2.5 py-4" aria-label="Κύρια πλοήγηση">
        <div className="space-y-5">
          {groups.map((group) => (
            <section key={group.label}>
              <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  return (
                    <li key={item.path}>
                      <Link
                        to={item.path}
                        onClick={onNavigate}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "group flex min-h-10 items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400",
                          active
                            ? "bg-white text-slate-950"
                            : "text-slate-400 hover:bg-white/[0.06] hover:text-white",
                        )}
                      >
                        <Icon className={cn("h-[18px] w-[18px] shrink-0", active ? "text-sky-700" : "text-slate-500 group-hover:text-slate-300")} />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {Number(item.count) > 0 && (
                          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[10px] font-bold text-white">
                            {Number(item.count) > 99 ? "99+" : item.count}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </nav>

      <div className="border-t border-white/10 p-3">
        <div className="space-y-1">
          {[
            { label: "Ροή lead", path: "/admin/journey", icon: GitBranch },
            { label: "Lineweb Updates", path: "/admin/changelog", icon: BookOpen },
          ].map(({ label, path, icon: Icon }) => (
            <Link
              key={path}
              to={path}
              onClick={onNavigate}
              className="flex min-h-11 items-center gap-2.5 rounded-xl px-3 text-xs font-medium text-slate-400 transition-colors hover:bg-white/[0.06] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            >
              <Icon className="h-4 w-4" />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
};

export default AdminSidebar;
